import {
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress
} from "@mui/material";
import React, { useEffect, useState, useRef, useMemo } from "react";
import AppLogo from "../../assets/image/CMS_LOGO.svg";
import "./style.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import { useFormik } from "formik";
import * as Yup from "yup";
import AxiosInstance from "../../utils/axiosInstance";
import Steps from "./Steps";
import { Link, useLocation, useNavigate } from "react-router-dom";
import InputText from "../InputFields/InputText";
import sendToast from "../Toast/sendToast";
import PasswordValidation from "../Password/PasswordValidation";
import Tooltip from "../../components/Tooltip/tooltip";
import showToast from "../../components/Toast/Toster";
import { useUserType } from "../../contexts/UserTypeContext";
import DonorLogo from "../../assets/image/DONOR_LOGO.svg";
import SignupImageDonor from "../../assets/image/donor-signup-image.jpg";
import SignupImage from "../../assets/image/Frame 427322463.png";
import { OrangeButton } from "../Icon/Index";
import { useRecaptchaV3 } from "../../hooks/useRecaptchaV3";

// SessionStorage helper functions
const saveStepData = (step, data) => {
  try {
    sessionStorage.setItem(`signupStep${step}Data`, JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving step ${step} data:`, error);
  }
};

const getStepData = (step) => {
  try {
    const savedData = sessionStorage.getItem(`signupStep${step}Data`);
    if (savedData) {
      return JSON.parse(savedData);
    }
  } catch (error) {
    console.error(`Error getting step ${step} data:`, error);
  }
  return null;
};

const clearStepData = (step) => {
  try {
    sessionStorage.removeItem(`signupStep${step}Data`);
  } catch (error) {
    console.error(`Error clearing step ${step} data:`, error);
  }
};

const clearAllStepData = () => {
  try {
    sessionStorage.removeItem("signupStep0Data");
    sessionStorage.removeItem("signupStep1Data");
    sessionStorage.removeItem("signupStep2Data");
  } catch (error) {
    console.error("Error clearing all step data:", error);
  }
};

// Function to handle navigation to login and clear signup data
const handleNavigateToLogin = (navigate, formik, setConfirmPasswordTouched) => {
  clearAllStepData();
  // Reset form values
  if (formik) {
    formik.setValues({
      EmailAddress: "",
      Password: "",
      cpassword: "",
    });
  }
  // Reset touched state
  if (setConfirmPasswordTouched) {
    setConfirmPasswordTouched(false);
  }
  navigate("/auth/login");
};

// Removed handleRefreshLogic - we don't want to clear data when navigating between steps
// Data should persist until the user completes signup or explicitly clears it
// Only clear data when user explicitly starts a new signup (navigates to /auth/signup without step param)

const SignUp = () => {
  useEffect(() => {
    window.onload = () => {};
    return () => {
      window.onload = null;
    };
  }, []);
  const { userType, logo } = useUserType();
  const isDonationMode = userType === "donor";
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showCPassword, setShowCPassword] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const step = queryParams.get("step");
  const signUpButtonRef = useRef(null);

  // Get active step number for progress bar (1, 2, or 3)
  const activeStep = useMemo(() => {
    if (!step || step === "1") return 1;
    if (step === "2") return 2;
    if (step === "3") return 3;
    return 1;
  }, [step]);
  const validatePassword = Yup.object({
    EmailAddress: Yup.string()
      .email("Invalid email")
      .required("Email is required")
      .matches(/^[^@]+@[^@]+\.[^@]+$/, "Email must contain '@' and '.'"),
    Password: PasswordValidation,
    cpassword: Yup.string()
      .oneOf([Yup.ref("Password"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  // Memoize initial values to prevent unnecessary re-renders
  const getInitialValues = useMemo(() => {
    if (!step) {
      const savedData = getStepData(0);
      if (savedData) {
        return {
          Password: savedData.Password || "",
          cpassword: savedData.cpassword || "",
          EmailAddress: savedData.EmailAddress || "",
        };
      }
    }
    return {
      Password: "",
      cpassword: "",
      EmailAddress: "",
    };
  }, [step]);

  const formik = useFormik({
    initialValues: getInitialValues,
    enableReinitialize: false, // Changed to false to prevent reinitialization during typing
    validationSchema: validatePassword,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  // Restore saved data only once on mount (when step is null)
  const hasRestoredData = useRef(false);
  useEffect(() => {
    if (!step && !hasRestoredData.current) {
      const savedData = getStepData(0);
      if (savedData) {
        hasRestoredData.current = true;
        // Use setValues to restore saved data only once
        formik.setValues({
          EmailAddress: savedData.EmailAddress || "",
          Password: savedData.Password || "",
          cpassword: savedData.cpassword || "",
        }, false); // false = don't validate on restore
      }
    }
    // Reset flag when step changes
    if (step) {
      hasRestoredData.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Auto-save form data whenever values change (only on step 0 - initial signup)
  // Use a ref to track the latest values to avoid stale closures
  const valuesRef = useRef(formik.values);
  useEffect(() => {
    valuesRef.current = formik.values;
  }, [formik.values]);

  useEffect(() => {
    if (!step) {
      // Debounce the save to avoid too many writes - increased to 1000ms for better performance
      const timeoutId = setTimeout(() => {
        // Only save if user has entered some data
        const currentValues = valuesRef.current;
        if (currentValues?.EmailAddress || currentValues?.Password || currentValues?.cpassword) {
          saveStepData(0, {
            EmailAddress: currentValues.EmailAddress || "",
            Password: currentValues.Password || "",
            cpassword: currentValues.cpassword || "",
          });
        }
      }, 1000); // Save after 1000ms of no changes for better performance

      return () => clearTimeout(timeoutId);
    }
  }, [formik.values.EmailAddress, formik.values.Password, formik.values.cpassword, step]);

  const [loader, setLoader] = useState(false);
  const { executeRecaptcha, isReady: recaptchaReady } = useRecaptchaV3();

  const handleSubmit = async (values) => {
    try {
      if (!recaptchaReady) {
        showToast.error("Security check is loading. Please try again in a moment.");
        return;
      }
      setLoader(true);

      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha("signup");
      } catch (_) {
        showToast.error("Security verification failed. Please try again.");
        setLoader(false);
        return;
      }

      const res = await AxiosInstance.post(`/v1/auth/check_user`, {
        EmailAddress: values?.EmailAddress,
        recaptchaToken,
      });

      if (res?.data?.statusCode === "200") {
        saveStepData(0, {
          EmailAddress: values?.EmailAddress,
          Password: values?.Password,
          cpassword: values?.cpassword,
        });
        navigate(`/auth/signup?step=2`);
      } else if (res?.data?.statusCode === 400) {
        showToast.warning(res?.data?.message);
      } else {
        showToast.warning(res?.data?.message);
      }
    } catch (error) {
      if (error?.response) {
        showToast.warning(
          error?.response?.data?.message || "We couldn't create your account. Please try again in a moment."
        );
      } else if (error?.request) {
        showToast.error("No response from the server, please try again later.");
      } else {
        showToast.error("Error occurred while sending request.");
      }
    } finally {
      setLoader(false);
    }
  };


  useEffect(() => {
    const handleWindowLoad = () => {};
    window.addEventListener("load", handleWindowLoad);
    return () => {
      window.removeEventListener("load", handleWindowLoad);
    };
  }, []);

  useEffect(() => {
    const imageLoaded = sessionStorage.getItem("imageLoaded");
    if (imageLoaded === "true") {
    }
  }, []);

  const confirmPasswordRef = useRef(null);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  
  // Real-time password match validation function
  const validatePasswordMatch = (password, confirmPassword) => {
    if (!confirmPassword || confirmPassword.trim() === "") {
      // Clear error if confirm password is empty
      formik.setFieldError("cpassword", undefined);
      return;
    }
    
    if (password === confirmPassword) {
      // Passwords match - clear error
      formik.setFieldError("cpassword", undefined);
    } else {
      // Passwords don't match - set error
      formik.setFieldError("cpassword", "Passwords must match");
    }
  };

  // Password onChange handler with real-time validation
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    formik.setFieldValue("Password", newPassword, false);
    
    // If confirm password has a value, validate match in real-time
    if (formik.values.cpassword) {
      validatePasswordMatch(newPassword, formik.values.cpassword);
    }
  };

  // Confirm password onChange handler with real-time validation
  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    formik.setFieldValue("cpassword", newConfirmPassword, false);
    
    // Mark as touched when user starts typing
    if (!confirmPasswordTouched && newConfirmPassword.length > 0) {
      setConfirmPasswordTouched(true);
    }
    
    // Validate match in real-time
    validatePasswordMatch(formik.values.Password, newConfirmPassword);
  };

  const handlePasswordKeyDown = (e) => {
    if (e?.key === "Tab") {
      e.preventDefault();
      if (confirmPasswordRef.current) {
        confirmPasswordRef.current.focus();
      } else {
        console.error("Confirm Password ref is not set");
      }
    }
  };

  const handleConfirmPasswordKeyDown = (e) => {
    if (e?.key === "Tab") {
      e.preventDefault();
      if (signUpButtonRef.current) {
        signUpButtonRef.current.focus();
      } else {
        console.error("Sign Up button ref is not set");
      }
    }
  };

  return (
    <>
      <Grid className="login signup" container>
        {!step ? (
          <Grid className="w-100 d-flex steps-container">
            <Grid
              className="steps-form-container"
              size={{
                xs: 12,
                md: 7
              }}>
              <Grid className="steps d-flex align-items-center justify-content-center" style={{ width: "100%", maxWidth: "100%" }}>
                <Grid className="my-steps d-flex align-items-center justify-content-center" style={{ width: "100%", maxWidth: "75%" }}>
                  <Grid className="app-logos mb-4 d-flex justify-content-center align-items-center" style={{ width: "100%" }}>
                    <Link to="/auth/login" onClick={() => {
                      clearAllStepData();
                      formik.setValues({
                        EmailAddress: "",
                        Password: "",
                        cpassword: "",
                      });
                      setConfirmPasswordTouched(false);
                    }}>
                      {userType === "donor" ? (
                        <img
                          src={DonorLogo}
                          alt="logo"
                          className="signup-logo-img"
                          style={{ width: "330px" }}
                          loading="eager"
                        />
                      ) : (
                        <img
                          src={AppLogo}
                          alt="logo"
                          className="signup-logo-img"
                          style={{ width: "330px" }}
                          loading="eager"
                        />
                      )}
                    </Link>
                  </Grid>
                  <form
                    style={{ width: "100%" }}
                    onSubmit={formik.handleSubmit}
                    className="formmm signup-form"
                  >
                    {/* Progress Bar - MUI Stepper */}
                    <Stepper
                      activeStep={activeStep - 1}
                      alternativeLabel
                      sx={{
                        width: '100%',
                        marginBottom: '10px',
                        marginTop: '10px',
                        '& .MuiStepLabel-root .Mui-completed': {
                          color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                        },
                        '& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel': {
                          color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                          fontWeight: 500,
                        },
                        '& .MuiStepLabel-root .Mui-active': {
                          color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                        },
                        '& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel': {
                          color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                          fontWeight: 500,
                        },
                        '& .MuiStepLabel-root .Mui-disabled': {
                          color: 'rgba(153, 153, 153, 1)',
                        },
                        '& .MuiStepLabel-label.Mui-disabled.MuiStepLabel-alternativeLabel': {
                          color: 'rgba(153, 153, 153, 1)',
                        },
                        '& .MuiStepConnector-root': {
                          top: '20px',
                          left: 'calc(-50% + 20px)',
                          right: 'calc(50% + 20px)',
                        },
                        '& .MuiStepConnector-line': {
                          borderTopWidth: '2px',
                          borderColor: isDonationMode ? 'rgba(104, 178, 203, 0.2)' : 'rgba(212, 148, 58, 0.2)',
                        },
                        '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                          borderColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                        },
                        '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                          borderColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                        },
                        '& .MuiStepIcon-root': {
                          color: isDonationMode ? 'rgba(104, 178, 203, 0.2)' : 'rgba(212, 148, 58, 0.2)',
                          fontSize: '2rem',
                          border: `2px solid ${isDonationMode ? 'rgba(104, 178, 203, 0.2)' : 'rgba(212, 148, 58, 0.2)'}`,
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          '&.Mui-active': {
                            color: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                            border: `2px solid ${isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)'}`,
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                          },
                          '&.Mui-completed': {
                            color: 'rgba(255, 255, 255, 1)',
                            border: '2px solid rgba(255, 255, 255, 1)',
                            backgroundColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                          },
                        },
                        '& .MuiStepIcon-text': {
                          fill: 'currentColor',
                          fontSize: '16px',
                          fontWeight: 600,
                          fontFamily: '"Poppins", sans-serif',
                        },
                        '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': {
                          fill: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                        },
                        '& .MuiStepIcon-root.Mui-completed .MuiStepIcon-text': {
                          fill: 'rgba(255, 255, 255, 1)',
                        },
                        '& .MuiStepLabel-label': {
                          fontFamily: '"Poppins", sans-serif',
                          fontSize: '14px',
                          fontWeight: 400,
                          marginTop: '10px',
                        },
                      }}
                    >
                      <Step>
                        <StepLabel
                          StepIconComponent={(props) => {
                            if (props.completed) {
                              return (
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                                    border: '2px solid rgba(255, 255, 255, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <CheckIcon style={{ color: 'rgba(255, 255, 255, 1)', fontSize: '20px' }} />
                                </div>
                              );
                            }
                            return (
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: props.active
                                    ? 'rgba(255, 255, 255, 1)'
                                    : 'rgba(255, 255, 255, 1)',
                                  border: `2px solid ${
                                    props.active
                                      ? isDonationMode
                                        ? 'rgba(104, 178, 203, 1)'
                                        : 'rgba(212, 148, 58, 1)'
                                      : isDonationMode
                                      ? 'rgba(104, 178, 203, 0.2)'
                                      : 'rgba(212, 148, 58, 0.2)'
                                  }`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color:
                                    props.active
                                      ? isDonationMode
                                        ? 'rgba(104, 178, 203, 1)'
                                        : 'rgba(212, 148, 58, 1)'
                                      : isDonationMode
                                      ? 'rgba(104, 178, 203, 0.3)'
                                      : 'rgba(212, 148, 58, 0.3)',
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  fontFamily: '"Poppins", sans-serif',
                                }}
                              >
                                {props.icon}
                        </div>
                            );
                          }}
                        >
                          Create Account
                        </StepLabel>
                      </Step>
                      <Step>
                        <StepLabel
                          StepIconComponent={(props) => {
                            if (props.completed) {
                              return (
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                                    border: '2px solid rgba(255, 255, 255, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <CheckIcon style={{ color: 'rgba(255, 255, 255, 1)', fontSize: '20px' }} />
                      </div>
                              );
                            }
                            return (
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                  border: `2px solid ${
                                    props.active
                                      ? isDonationMode
                                        ? 'rgba(104, 178, 203, 1)'
                                        : 'rgba(212, 148, 58, 1)'
                                      : isDonationMode
                                      ? 'rgba(104, 178, 203, 0.2)'
                                      : 'rgba(212, 148, 58, 0.2)'
                                  }`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color:
                                    props.active
                                      ? isDonationMode
                                        ? 'rgba(104, 178, 203, 1)'
                                        : 'rgba(212, 148, 58, 1)'
                                      : isDonationMode
                                      ? 'rgba(104, 178, 203, 0.3)'
                                      : 'rgba(212, 148, 58, 0.3)',
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  fontFamily: '"Poppins", sans-serif',
                                }}
                              >
                                {props.icon}
                        </div>
                            );
                          }}
                        >
                          Activate
                        </StepLabel>
                      </Step>
                      <Step>
                        <StepLabel
                          StepIconComponent={(props) => {
                            if (props.completed) {
                              return (
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: isDonationMode ? 'rgba(104, 178, 203, 1)' : 'rgba(212, 148, 58, 1)',
                                    border: '2px solid rgba(255, 255, 255, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <CheckIcon style={{ color: 'rgba(255, 255, 255, 1)', fontSize: '20px' }} />
                      </div>
                              );
                            }
                            return (
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                  border: `2px solid ${
                                    props.active
                                      ? isDonationMode
                                        ? 'rgba(104, 178, 203, 1)'
                                        : 'rgba(212, 148, 58, 1)'
                                      : isDonationMode
                                      ? 'rgba(104, 178, 203, 0.2)'
                                      : 'rgba(212, 148, 58, 0.2)'
                                  }`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color:
                                    props.active
                                      ? isDonationMode
                                        ? 'rgba(104, 178, 203, 1)'
                                        : 'rgba(212, 148, 58, 1)'
                                      : isDonationMode
                                      ? 'rgba(104, 178, 203, 0.3)'
                                      : 'rgba(212, 148, 58, 0.3)',
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  fontFamily: '"Poppins", sans-serif',
                                }}
                              >
                                {props.icon}
                        </div>
                            );
                          }}
                        >
                          Business Setup
                        </StepLabel>
                      </Step>
                    </Stepper>
                    <Typography
                      className="text signup-heading"
                      style={{ fontSize: "26px", marginTop: "10px",marginBottom: "10px" }}
                    >
                      Sign Up
                    </Typography>
                    <FormGroup style={{ width: "100%", marginTop: "8px" }}>
                      {/* <label className="signup-form-label" style={{ fontSize: "18px", fontWeight: "500", lineHeight: "21px", textAlign: "left", marginTop: "8px" ,marginBottom: "12px"}}>Email</label> */}
                      <InputText
                        value={formik?.values?.EmailAddress || ""}
                        onChange={(e) => {
                          const lowercaseValue = e.target.value.toLowerCase();
                          formik?.setFieldValue("EmailAddress", lowercaseValue);
                        }}
                        onBlur={formik?.handleBlur}
                        error={
                          formik?.touched?.EmailAddress &&
                          Boolean(formik?.errors?.EmailAddress)
                        }
                        helperText={
                          formik?.touched?.EmailAddress &&
                          formik?.errors?.EmailAddress
                        }
                        name="EmailAddress"
                        label="Email"
                        required
                        type="email"
                        className="text-blue-color w-100 "
                        fieldHeight="44px"
                        placeholder=" Enter abc@example.com"
                        autoComplete="email"
                      />
                    </FormGroup>
                    <FormGroup
                      style={{
                        width: "100%",
                        marginTop: "16px",
                      }}
                      className="textboxes"
                    >
                      <InputText
                        value={formik?.values?.Password || ""}
                        onChange={handlePasswordChange}
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          // Validate confirm password match when password field loses focus
                          if (formik.values.cpassword) {
                            validatePasswordMatch(e.target.value, formik.values.cpassword);
                          }
                        }}
                        error={
                          formik?.touched?.Password &&
                          Boolean(formik?.errors?.Password)
                        }
                        helperText={
                          formik?.touched?.Password && formik?.errors?.Password
                        }
                        name="Password"
                        label="Password"
                        required
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        className="text-blue-color w-100"
                        fieldHeight="44px"
                        autoComplete="off"
                        onKeyDown={handlePasswordKeyDown}
                        endAdornment={
                          <InputAdornment
                            position="end"
                            style={{ gap: "10px" }}
                          >
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              tabIndex={-1}
                              disableRipple
                              sx={{ "&:focus": { outline: "none" } }}
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                            <Tooltip
                              title="Password must be at least 8 characters long"
                              disableFocusListener
                              disableTouchListener
                              disableInteractive
                              PopperProps={{
                                disablePortal: true,
                                sx: {
                                  pointerEvents: "none",
                                  "& .MuiTooltip-tooltip": {
                                    pointerEvents: "none",
                                    tabIndex: -1,
                                  },
                                },
                              }}
                            >
                              <span
                                aria-hidden="true"
                                tabIndex={-1}
                                style={{
                                  display: "inline-block",
                                  pointerEvents: "none",
                                }}
                              />
                            </Tooltip>
                          </InputAdornment>
                        }
                      />
                    </FormGroup>
                    <FormGroup
                      style={{
                        width: "100%",
                        marginTop: "16px",
                        // display: "flex",
                      }}
                      className="reenterpassword"
                    >
                      {/* <label className="signup-form-label" style={{ fontSize: "18px", fontWeight: "500", lineHeight: "21px", textAlign: "left", marginTop: "14px" ,marginBottom: "12px"}}>Confirm Password</label> */}

                      <InputText
                        inputRef={confirmPasswordRef}
                        id="cpassword"
                        value={formik?.values?.cpassword || ""}
                        onChange={handleConfirmPasswordChange}
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          setConfirmPasswordTouched(true);
                          // Final validation on blur
                          validatePasswordMatch(formik.values.Password, e.target.value);
                        }}
                        error={
                          (confirmPasswordTouched || formik?.touched?.cpassword) &&
                          Boolean(formik?.errors?.cpassword)
                        }
                        helperText={
                          (confirmPasswordTouched || formik?.touched?.cpassword) &&
                          formik?.errors?.cpassword
                        }
                        name="cpassword"
                        label="Confirm Password"
                        required
                        placeholder="Confirm password"
                        type={showCPassword ? "text" : "password"}
                        className="text-blue-color w-100"
                        fieldHeight="44px"
                        autoComplete="new-password"
                        onPaste={(e) => {
                          e.preventDefault();
                          // Handle paste manually to trigger validation
                          const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                          const syntheticEvent = {
                            target: { value: pastedText, name: 'cpassword' }
                          };
                          handleConfirmPasswordChange(syntheticEvent);
                        }}
                        onCopy={(e) => e.preventDefault()}
                        onCut={(e) => e.preventDefault()}
                        onKeyDown={handleConfirmPasswordKeyDown}
                        endAdornment={
                          <InputAdornment
                            position="end"
                            style={{ gap: "10px" }}
                          >
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowCPassword(!showCPassword)}
                              edge="end"
                              tabIndex={-1}
                              disableRipple
                              sx={{ "&:focus": { outline: "none" } }}
                            >
                              {showCPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                            <Tooltip
                              title="Re-enter your password"
                              disableFocusListener
                              disableTouchListener
                              disableInteractive
                              PopperProps={{
                                disablePortal: true,
                                sx: {
                                  pointerEvents: "none",
                                  "& .MuiTooltip-tooltip": {
                                    pointerEvents: "none",
                                    tabIndex: -1,
                                  },
                                },
                              }}
                            >
                              <span
                                aria-hidden="true"
                                tabIndex={-1}
                                style={{
                                  display: "inline-block",
                                  pointerEvents: "none",
                                }}
                              />
                            </Tooltip>
                          </InputAdornment>
                        }
                      />
                    </FormGroup>
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        lineHeight: "21px",
                        textAlign: "center",
                        marginTop: "13px",
                      }}
                    >
                      Do you have an account?{" "}
                      <span
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() => handleNavigateToLogin(navigate, formik, setConfirmPasswordTouched)}
                        className={`${
                          userType === "donor"
                            ? "text-donor-color"
                            : "text-orange-color"
                        }`}
                      >
                        Log In
                      </span>
                    </Typography>
                    <FormGroup style={{ width: "100%", marginTop: "15px" }}>
                      <OrangeButton
                        ref={signUpButtonRef}
                        type="submit"
                        disabled={loader || !recaptchaReady}
                        isDonationMode={isDonationMode}
                        className="w-100 signup-primary-submit"
                        style={{
                          color: "#fff",
                          fontSize: "14px",
                          fontWeight: "600",
                          lineHeight: "21px",
                          height: "48px",
                          minHeight: "48px",
                          padding: "0 16px",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxSizing: "border-box",
                          borderRadius: "4px",
                        }}
                        label={
                          loader ? (
                            <CircularProgress size={30} color="inherit" />
                          ) : !recaptchaReady ? (
                            "Loading..."
                          ) : (
                            "Sign Up"
                          )
                        }
                      />
                    </FormGroup>
                    {recaptchaReady && (
                      <Typography
                        variant="caption"
                        style={{
                          display: "block",
                          marginTop: "8px",
                          color: "#666",
                          fontSize: "11px",
                          textAlign: "center",
                        }}
                      >
                        This page is protected by reCAPTCHA and subject to Google's{" "}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#666" }}>Privacy Policy</a>
                        {" "}and{" "}
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#666" }}>Terms of Service</a>.
                      </Typography>
                    )}
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        lineHeight: "21px",
                        textAlign: "center",
                        marginTop: "13px",
                        color: "rgba(49, 49, 49, 1)",
                      }}
                    >
                      By signing up, you agree to receive updates from us. You
                      can unsubscribe anytime. See our
                      <Link
                        to={
                          userType === "donor" || isDonationMode
                            ? "https://clouddonormanager.com/privacy-policy/"
                            : "https://cloudjobmanager.com/privacy-policy/"
                        }
                        target="_blank"
                        style={{ textDecoration: "none" }}
                      >
                        <span
                          style={{
                            fontWeight: "600",
                            marginLeft: "5px",
                            marginRight: "5px",
                          }}
                          className="text-blue-color"
                        >
                          Privacy Policy
                        </span>
                      </Link>
                      and
                      <Link
                        to={
                          userType === "donor" || isDonationMode
                            ? "https://clouddonormanager.com/terms-of-service/"
                            : "https://cloudjobmanager.com/terms-and-conditions/"
                        }
                        target="_blank"
                        style={{ textDecoration: "none" }}
                      >
                        <span
                          style={{
                            fontWeight: "600",
                            marginLeft: "5px",
                            marginRight: "5px",
                          }}
                          className="text-blue-color"
                        >
                          Terms of Service
                        </span>
                      </Link>{" "}
                      for more information.
                    </Typography>
                  </form>
                </Grid>
              </Grid>
            </Grid>

            <Grid
              className="main-imageeee img signup-side-image"
              size={{
                xs: 12,
                md: 5
              }}>
              <img
                src={userType === "donor" ? SignupImageDonor : SignupImage}
                alt="Signup"
                className="signup-side-image-img"
                style={{
                  height: "100vh",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
            </Grid>
          </Grid>
        ) : (
          <>
            <Steps
              EmailAddress={formik?.values?.EmailAddress || getStepData(0)?.EmailAddress}
              Password={formik?.values?.Password || getStepData(0)?.Password}
              isDonationMode={isDonationMode}
            />
          </>
        )}
      </Grid>
    </>
  );
};

export default SignUp;
